import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { SpaceItem, UserInfo } from '@/types'
import { isMockSpaceId } from '@/lib/currentOrg'
import { ensureSpaceInList } from '@/lib/spaces'

export type SpacesLoad = 'pending' | 'ready' | 'error'

/** GET /developer/profile 在本次会话里只拉一次 */
export type ProfileStatus = 'idle' | 'loading' | 'ready' | 'error'

/** 存在内存里的开发者资料。displayAvatar 可能是 blob，不要写入 sessionStorage。 */
export interface DeveloperProfileState {
  name: string
  website: string
  developerNo: string
  avatarUrl: string
  imAvatarUrl: string
  displayAvatar: string
}

interface UserState {
  user: UserInfo | null
  developerProfile: DeveloperProfileState | null
  profileStatus: ProfileStatus
  space_id: string | null
  /** 当前账号已加入的空间（`GET /org/main/list`） */
  spaces: SpaceItem[]
  /** 组织列表是否已经返回。未完成前不要发依赖当前空间的请求 */
  spacesLoad: SpacesLoad
  isAuthenticated: boolean
  enter: (payload: { userId: string; space_id: string }) => void
  /** 仅更新当前空间（顶栏切换 / 宿主切空间时） */
  setSpaceId: (space_id: string) => void
  /** 用接口列表整表替换，并写入解析后的当前空间 */
  replaceSpaces: (spaces: SpaceItem[], space_id: string | null) => void
  /** 组织列表失败：清空列表，禁止回落 mock 空间 */
  failSpaceLoad: () => void
  updateProfile: (patch: Partial<UserInfo>) => void
  /** 写入本次会话的开发者资料，并同步到顶栏用的 user。userAvatar 不使用 blob。 */
  applyDeveloperProfile: (profile: DeveloperProfileState, userAvatar: string) => void
  logout: () => void
}

function accountUser(userId: string, current: UserInfo | null): UserInfo {
  return {
    id: userId,
    name: current?.id === userId ? current.name : '',
    email: current?.id === userId ? current.email : '',
    avatar: current?.id === userId ? current.avatar : '',
    website: current?.id === userId ? current.website : undefined,
    developer_id: current?.id === userId ? current.developer_id : undefined,
  }
}

function stripLegacySpaces(spaces: SpaceItem[]): SpaceItem[] {
  return spaces.filter((space) => !isMockSpaceId(space.id))
}

function realSpaceId(space_id: string | null | undefined): string | null {
  if (!space_id || isMockSpaceId(space_id)) return null
  return space_id
}

/** 当前会话：按账号 + 空间隔离，存在 sessionStorage（关标签即失效） */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      developerProfile: null,
      profileStatus: 'idle',
      space_id: null,
      spaces: [],
      spacesLoad: 'pending',
      isAuthenticated: false,
      enter: ({ userId, space_id }) => {
        const nextId = realSpaceId(space_id)
        const spaces = stripLegacySpaces(get().spaces)
        set({
          user: accountUser(userId, get().user),
          space_id: nextId,
          spaces: nextId ? ensureSpaceInList(spaces, nextId) : spaces,
          isAuthenticated: true,
        })
      },
      setSpaceId: (space_id) => {
        const nextId = realSpaceId(space_id)
        if (!nextId) return
        set({
          space_id: nextId,
          spaces: ensureSpaceInList(stripLegacySpaces(get().spaces), nextId),
        })
      },
      replaceSpaces: (spaces, space_id) =>
        set({
          spaces: stripLegacySpaces(spaces),
          space_id: space_id && isMockSpaceId(space_id) ? null : space_id,
          spacesLoad: 'ready',
        }),
      failSpaceLoad: () =>
        set({ spaces: [], space_id: null, spacesLoad: 'error' }),
      updateProfile: (patch) => {
        const current = get().user
        if (!current) return
        set({ user: { ...current, ...patch, id: current.id } })
      },
      applyDeveloperProfile: (profile, userAvatar) => {
        if (!get().isAuthenticated) return
        const current = get().user
        set({
          developerProfile: profile,
          profileStatus: 'ready',
          user: {
            id: current?.id ?? '',
            email: current?.email ?? '',
            name: profile.name,
            website: profile.website || undefined,
            avatar: userAvatar,
            developer_id: profile.developerNo || current?.developer_id,
          },
        })
      },
      logout: () =>
        set({
          user: null,
          developerProfile: null,
          profileStatus: 'idle',
          space_id: null,
          spaces: [],
          spacesLoad: 'pending',
          isAuthenticated: false,
        }),
    }),
    {
      name: 'hibro-session-dev',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        space_id: state.space_id,
        isAuthenticated: state.isAuthenticated,
        // 空间列表以接口为准，不持久化，避免刷新后回落到历史 mock
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<UserState>
        const space_id =
          p.space_id && isMockSpaceId(p.space_id) ? null : (p.space_id ?? null)
        return {
          ...current,
          ...p,
          space_id,
          spaces: [],
          spacesLoad: 'pending',
        }
      },
    },
  ),
)
